import { toHex, type RGBA } from '../lib/color';
import { load, save } from '../lib/storage';
import { toast } from './toast';
import { t } from '../lib/i18n';

const HISTORY_KEY = 'color-picker:history';
const FAVORITES_KEY = 'color-picker:favorites';
const HISTORY_MAX = 20;

export interface HistoryRef {
  root: HTMLElement;
  record: (c: RGBA) => void;
  update: () => void;
}

function isSameColor(a: RGBA, b: RGBA): boolean {
  return a.r === b.r && a.g === b.g && a.b === b.b && Math.abs(a.a - b.a) < 0.005;
}

interface RowDef {
  titleKey: 'History' | 'Favorites';
  storageKey: string;
  canClear: boolean;
  onPick: (c: RGBA) => void;
}

export function createHistory(
  onPick: (c: RGBA) => void
): HistoryRef {
  const root = document.createElement('section');
  root.className = 'history';

  const historyList: RGBA[] = load<RGBA[]>(HISTORY_KEY, []);
  const favorites: RGBA[] = load<RGBA[]>(FAVORITES_KEY, []);

  const historyEl = createRow({
    titleKey: 'History',
    storageKey: HISTORY_KEY,
    canClear: true,
    onPick
  }, historyList);
  const favEl = createRow({
    titleKey: 'Favorites',
    storageKey: FAVORITES_KEY,
    canClear: true,
    onPick
  }, favorites);

  root.append(historyEl.root, favEl.root);

  function update(): void {
    historyEl.render();
    favEl.render();
  }

  function record(c: RGBA): void {
    // 去重：连续相同色不重复加
    if (historyList[0] && isSameColor(historyList[0], c)) {
      historyEl.render();
      return;
    }
    historyList.unshift(c);
    if (historyList.length > HISTORY_MAX) historyList.length = HISTORY_MAX;
    save(HISTORY_KEY, historyList);
    historyEl.render();
  }

  function toggleFavorite(c: RGBA): void {
    const idx = favorites.findIndex((x) => isSameColor(x, c));
    if (idx >= 0) {
      favorites.splice(idx, 1);
      toast(t('Removed from favorites'));
    } else {
      favorites.unshift(c);
      toast(t('Saved {hex}', { hex: toHex(c) }));
    }
    save(FAVORITES_KEY, favorites);
    favEl.render();
  }

  // 收藏按钮放外面调用
  (root as HTMLElement & { _toggleFavorite?: (c: RGBA) => void })._toggleFavorite = toggleFavorite;

  return { root, record, update };
}

interface RowHandle {
  root: HTMLElement;
  render: () => void;
}

function createRow(def: RowDef, list: RGBA[]): RowHandle {
  const wrap = document.createElement('div');
  wrap.className = 'history__row';

  const header = document.createElement('div');
  header.className = 'history__head';
  const title = document.createElement('span');
  title.className = 'history__title';
  title.textContent = t(def.titleKey);
  header.appendChild(title);
  let clearBtn: HTMLButtonElement | undefined;
  if (def.canClear) {
    clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'btn btn--ghost btn--xs';
    clearBtn.textContent = t('Clear');
    clearBtn.addEventListener('click', () => {
      list.length = 0;
      save(def.storageKey, list);
      render();
    });
    header.appendChild(clearBtn);
  }

  const swatches = document.createElement('div');
  swatches.className = 'history__swatches';

  function render(): void {
    title.textContent = t(def.titleKey);
    clearBtn!.textContent = t('Clear');
    swatches.replaceChildren();
    if (list.length === 0) {
      const empty = document.createElement('span');
      empty.className = 'history__empty';
      if (def.titleKey === 'Favorites') {
        empty.textContent = 'Click ⭐ to save colors';
        empty.title = 'Click ⭐ above to add to favorites';
      } else {
        empty.textContent = 'Pick a color to start';
        empty.title = 'Use Eyedropper or Image Pick to begin';
      }
      swatches.appendChild(empty);
      return;
    }
    for (const c of list) {
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'history__cell';
      cell.style.background = c.a < 1 ? `rgba(${c.r},${c.g},${c.b},${c.a})` : toHex(c);
      cell.title = toHex(c);
      cell.addEventListener('click', () => def.onPick(c));
      swatches.appendChild(cell);
    }
  }

  wrap.append(header, swatches);
  render();

  return { root: wrap, render };
}
